import { defineStore } from 'pinia';
import DealAPI from 'dashboard/api/deals';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import camelcaseKeys from 'camelcase-keys';

export const useDealsStore = defineStore('deals', {
  state: () => ({
    records: [],
    selectedDeal: null,
    pipelineSummary: null,
    searchResults: [],
    uiFlags: {
      fetchingList: false,
      fetchingItem: false,
      creating: false,
      updating: false,
      deleting: false,
      fetchingSummary: false,
      searching: false,
    },
    meta: {},
    currentFilters: {
      page: 1,
      sort: 'updated_at_desc',
      stage: '',
      search: '',
    },
  }),

  getters: {
    getDealsList: state => camelcaseKeys(state.records, { deep: true }),
    getSelectedDeal: state => {
      return state.selectedDeal ? camelcaseKeys(state.selectedDeal, { deep: true }) : null;
    },
    getUIFlags: state => state.uiFlags,
    getMeta: state => state.meta,
    getPipelineSummary: state => state.pipelineSummary,
    getSearchResults: state => camelcaseKeys(state.searchResults, { deep: true }),
    getCurrentFilters: state => state.currentFilters,
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    setMeta(meta) {
      this.meta = meta;
    },

    setFilters(filters) {
      this.currentFilters = { ...this.currentFilters, ...filters };
    },

    async get({ page, sort, stage, search } = {}) {
      const params = {
        page: page ?? this.currentFilters.page,
        sort: sort ?? this.currentFilters.sort,
        stage: stage ?? this.currentFilters.stage,
        search: search ?? this.currentFilters.search,
      };
      
      this.setFilters(params);
      this.setUIFlag({ fetchingList: true });
      
      try {
        const {
          data: { payload, meta },
        } = await DealAPI.get(params);
        this.records = payload;
        this.setMeta(meta);
      } catch (error) {
        throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async search({ q = '', limit = 10 }) {
      if (!q || q.length < 2) {
        this.searchResults = [];
        return;
      }
      
      this.setUIFlag({ searching: true });
      try {
        const { data } = await DealAPI.search({ q, limit });
        this.searchResults = data.payload || data || [];
        return this.searchResults;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ searching: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      try {
        const { data } = await DealAPI.show(id);
        this.selectedDeal = data.payload || data;
        return data.payload || data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.uiFlags.fetchingItem = false;
      }
    },

    async create(dealData) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await DealAPI.create(dealData);
        const deal = data.payload || data;
        this.records.unshift(deal);
        return deal;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async update(id, dealData) {
      this.setUIFlag({ updating: true });
      try {
        const { data } = await DealAPI.update(id, dealData);
        const deal = data.payload || data;
        const index = this.records.findIndex(d => d.id === id);
        if (index !== -1) {
          this.records[index] = deal;
        }
        if (this.selectedDeal?.id === id) {
          this.selectedDeal = deal;
        }
        return deal;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ updating: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deleting: true });
      try {
        await DealAPI.delete(id);
        this.records = this.records.filter(d => d.id !== id);
        if (this.selectedDeal?.id === id) {
          this.selectedDeal = null;
        }
        return true;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },

    async fetchPipelineSummary() {
      this.setUIFlag({ fetchingSummary: true });
      try {
        const { data } = await DealAPI.pipelineSummary();
        this.pipelineSummary = data;
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ fetchingSummary: false });
      }
    },

    clearSelectedDeal() {
      this.selectedDeal = null;
    },

    clearSearch() {
      this.searchResults = [];
    },
  },
});
